import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Info, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import Layout from '../../components/layout/AppLayout';
import Header from '../../../shared/components/layout/Header';
import { useSmartBack } from '../../../shared/navigation';
import Loader from '../../../shared/components/ui/Loader';
import Modal from '../../../shared/components/ui/Modal';
import {
  getSalaryComponents,
  saveSalaryComponent,
  getStatutoryConfig,
  saveStatutoryConfig,
  previewSalaryStructure
} from '../../services/api/implementation/impl';
import {
  Callout
} from '../../../shared/components/ui/primitives';

const TABS = [
  { key: 'components', label: 'Salary components' },
  { key: 'statutory', label: 'Statutory rules' }
];

const CALCULATIONS = [
  { value: 'FIXED', label: 'Fixed amount' },
  { value: 'PERCENT_OF_GROSS', label: '% of gross' },
  { value: 'PERCENT_OF_BASIC', label: '% of basic' },
  { value: 'BALANCE', label: 'Balance (absorbs the remainder)' }
];

/**
 * Payroll configuration.
 *
 * Nothing is seeded, and both tabs say so. Statutory rates in particular look
 * like universal constants and are not — whether PF is restricted to the
 * ceiling is an employer choice, and professional tax differs by state with
 * several levying none. A default would deduct the wrong amount from real
 * salaries while looking authoritative.
 */
const PayrollSetup = () => {
  const { onBack } = useSmartBack({ fallback: '/builder/hr/payroll' });
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('components');
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const components = useQuery({
    queryKey: ['salaryComponents'],
    queryFn: getSalaryComponents
  });

  const rows = components.data ?? [];

  return (
    <Layout>
      <Header backArrow onBack={onBack} data={{ name: 'Payroll Setup' }} />

      <div className="p-4 space-y-4">
        <div className="flex gap-2 border-b border-gray-200">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                tab === t.key
                  ? 'border-brand-700 text-brand-800'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'components' ? (
          <>
            <Callout tone="info" collapsible icon={<Info size={14} />} title="How components resolve">
              Fixed amounts, then % of gross, then % of basic, then a{' '}
              <strong>balance</strong> component that absorbs whatever is left so
              the total lands exactly on the agreed gross. Without one, structures
              drift by a few rupees.
            </Callout>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setCreating(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-700 text-white text-sm font-medium"
              >
                <Plus size={16} /> Add component
              </button>
            </div>

            {components.isFetching ? (
              <Loader />
            ) : rows.length === 0 ? (
              <div className="bg-white border border-gray-200 rounded-lg py-10 text-center">
                <p className="text-gray-600 font-medium">
                  No salary components configured.
                </p>
                <p className="text-sm text-gray-500 mt-1 max-w-lg mx-auto">
                  Nothing is set up by default — structures are company policy.
                  Until components exist, no salary can be assigned and payroll
                  cannot run. A typical set is Basic, HRA and a Special Allowance
                  as the balance.
                </p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto bg-white rounded-lg border border-gray-200">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-gray-600">
                      <tr>
                        <Th>#</Th>
                        <Th>Name</Th>
                        <Th>Code</Th>
                        <Th>Type</Th>
                        <Th>Calculation</Th>
                        <Th>PF</Th>
                        <Th>ESI</Th>
                        <Th>Pro-rated</Th>
                        <Th />
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((component) => (
                        <tr key={component.id} className="border-t border-gray-100">
                          <Td>{component.sequence}</Td>
                          <Td className="font-medium text-gray-900">
                            {component.name}
                          </Td>
                          <Td>{component.code}</Td>
                          <Td>{titleCase(component.type)}</Td>
                          <Td>
                            {describeCalculation(component)}
                          </Td>
                          <Td>{component.pf_applicable ? 'Yes' : '—'}</Td>
                          <Td>{component.esi_applicable ? 'Yes' : '—'}</Td>
                          <Td>{component.prorated ? 'Yes' : 'No'}</Td>
                          <Td>
                            <button
                              type="button"
                              onClick={() => setEditing(component)}
                              className="text-brand-700 text-xs underline"
                            >
                              Edit
                            </button>
                          </Td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <StructurePreview />
              </>
            )}
          </>
        ) : (
          <StatutoryTab />
        )}
      </div>

      {(creating || editing) && (
        <ComponentModal
          component={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: ['salaryComponents'] });
            setCreating(false);
            setEditing(null);
          }}
        />
      )}
    </Layout>
  );
};

/**
 * Try a gross against the components before assigning it to anybody.
 *
 * Worth having because a misconfigured structure is invisible until it is on
 * somebody's payslip, and by then it has been paid.
 */
const StructurePreview = () => {
  const [gross, setGross] = useState(50000);

  const preview = useMutation({
    mutationFn: () => previewSalaryStructure(Number(gross)),
    onError: (err) =>
      toast.error(err?.response?.data?.message || 'Could not build the preview')
  });

  const result = preview.data;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="font-medium text-gray-800 mb-2">Try a salary</div>
      <div className="flex gap-2 items-end">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Monthly gross
          </label>
          <input
            type="number"
            value={gross}
            onChange={(e) => setGross(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-40"
          />
        </div>
        <button
          type="button"
          onClick={() => preview.mutate()}
          disabled={preview.isPending}
          className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium disabled:opacity-50"
        >
          {preview.isPending ? 'Resolving…' : 'Preview'}
        </button>
      </div>

      {result && (
        <div className="mt-3">
          {result.overflow > 0 && (
            <div className="flex items-center gap-2 rounded border border-danger-border bg-danger-bg px-3 py-2 text-xs text-danger-fg mb-2">
              <AlertTriangle size={15} />
              Fixed and percentage components exceed the gross by ₹{result.overflow}.
            </div>
          )}
          <table className="min-w-full text-sm">
            <tbody>
              {result.lines.map((line) => (
                <tr key={line.code} className="border-t border-gray-100">
                  <Td>{line.name}</Td>
                  <Td className="text-right">{money(line.amount)}</Td>
                </tr>
              ))}
              <tr className="border-t border-gray-200 font-semibold">
                <Td>Total</Td>
                <Td className="text-right">
                  {money(
                    result.lines
                      .filter((l) => l.type === 'EARNING')
                      .reduce((sum, l) => sum + Number(l.amount), 0)
                  )}
                </Td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const StatutoryTab = () => {
  const queryClient = useQueryClient();
  const { data, isFetching } = useQuery({
    queryKey: ['statutoryConfig'],
    queryFn: getStatutoryConfig
  });

  const [form, setForm] = useState(null);

  const current = form ?? data ?? defaultStatutory();

  const save = useMutation({
    mutationFn: saveStatutoryConfig,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['statutoryConfig'] });
      toast.success('Statutory rules saved');
      setForm(null);
    },
    onError: (err) => toast.error(err?.response?.data?.message || 'Could not save')
  });

  const set = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm({ ...current, [key]: value });
  };

  if (isFetching) return <Loader />;

  return (
    <div className="space-y-4">
      <Callout
        tone="warning"
        collapsible
        icon={<AlertTriangle size={14} />}
        title="Check these against your own registrations">
        <div>
          These are not universal constants. Whether PF is restricted to the
          ceiling is your choice as an employer, and professional tax slabs
          differ by state — several states levy none at all.
          <div className="mt-1">
            Saving creates a <strong>new dated version</strong>, so past payslips
            keep the rules they were computed with.
          </div>
        </div>
      </Callout>

      {!data && (
        <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm text-gray-600">
          No statutory configuration exists yet. Payroll cannot run until this is
          saved once.
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
        <Field label="Effective from">
          <input
            type="date"
            value={dateInput(current.effective_from)}
            onChange={set('effective_from')}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
        </Field>

        <Section title="Provident Fund">
          <Check label="PF applicable" checked={current.pf_enabled} onChange={set('pf_enabled')} />
          {current.pf_enabled && (
            <Grid>
              <Num label="Employee rate %" value={current.pf_employee_rate} onChange={set('pf_employee_rate')} />
              <Num label="Employer rate %" value={current.pf_employer_rate} onChange={set('pf_employer_rate')} />
              <Num label="Wage ceiling" value={current.pf_wage_ceiling} onChange={set('pf_wage_ceiling')} />
              <Num label="Pension rate %" value={current.eps_rate} onChange={set('eps_rate')} />
              <div className="sm:col-span-2">
                <Check
                  label="Restrict contributions to the wage ceiling"
                  checked={current.pf_restrict_to_ceiling}
                  onChange={set('pf_restrict_to_ceiling')}
                  hint="Off means PF is calculated on full basic. Both are lawful and they differ by thousands of rupees."
                />
              </div>
            </Grid>
          )}
        </Section>

        <Section title="ESI">
          <Check label="ESI applicable" checked={current.esi_enabled} onChange={set('esi_enabled')} />
          {current.esi_enabled && (
            <Grid>
              <Num label="Employee rate %" value={current.esi_employee_rate} onChange={set('esi_employee_rate')} />
              <Num label="Employer rate %" value={current.esi_employer_rate} onChange={set('esi_employer_rate')} />
              <Num label="Wage ceiling" value={current.esi_wage_ceiling} onChange={set('esi_wage_ceiling')} />
            </Grid>
          )}
        </Section>

        <Section title="Professional Tax">
          <Check label="Professional tax applicable" checked={current.pt_enabled} onChange={set('pt_enabled')} />
          {current.pt_enabled && (
            <>
              <Grid>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    State
                  </label>
                  <input
                    type="text"
                    value={current.pt_state ?? ''}
                    onChange={set('pt_state')}
                    placeholder="Madhya Pradesh"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
              </Grid>
              <SlabEditor
                slabs={current.pt_slabs ?? []}
                onChange={(slabs) => setForm({ ...current, pt_slabs: slabs })}
              />
            </>
          )}
        </Section>

        <Section title="Labour Welfare Fund">
          <Check label="LWF applicable" checked={current.lwf_enabled} onChange={set('lwf_enabled')} />
          {current.lwf_enabled && (
            <Grid>
              <Num label="Employee amount" value={current.lwf_employee} onChange={set('lwf_employee')} />
              <Num label="Employer amount" value={current.lwf_employer} onChange={set('lwf_employer')} />
            </Grid>
          )}
        </Section>

        <div className="flex justify-end">
          <button
            type="button"
            disabled={save.isPending}
            onClick={() => save.mutate(current)}
            className="px-5 py-2 rounded-lg bg-brand-700 text-white text-sm font-medium disabled:opacity-50"
          >
            {save.isPending ? 'Saving…' : 'Save as new version'}
          </button>
        </div>
      </div>
    </div>
  );
};

const SlabEditor = ({ slabs, onChange }) => {
  const update = (index, key, value) => {
    const next = slabs.map((slab, i) =>
      i === index
        ? { ...slab, [key]: value === '' ? null : Number(value) }
        : slab
    );
    onChange(next);
  };

  return (
    <div className="mt-2">
      <div className="text-xs font-medium text-gray-600 mb-1">
        Monthly slabs on gross
      </div>
      <table className="text-sm">
        <thead>
          <tr className="text-gray-500 text-xs">
            <th className="text-left pr-3 pb-1">From</th>
            <th className="text-left pr-3 pb-1">To (blank = no limit)</th>
            <th className="text-left pb-1">Tax</th>
          </tr>
        </thead>
        <tbody>
          {slabs.map((slab, index) => (
            <tr key={index}>
              <td className="pr-3 pb-1">
                <input
                  type="number"
                  value={slab.from ?? ''}
                  onChange={(e) => update(index, 'from', e.target.value)}
                  className="border border-gray-300 rounded px-2 py-1 w-28"
                />
              </td>
              <td className="pr-3 pb-1">
                <input
                  type="number"
                  value={slab.to ?? ''}
                  onChange={(e) => update(index, 'to', e.target.value)}
                  className="border border-gray-300 rounded px-2 py-1 w-28"
                />
              </td>
              <td className="pb-1">
                <input
                  type="number"
                  value={slab.amount ?? ''}
                  onChange={(e) => update(index, 'amount', e.target.value)}
                  className="border border-gray-300 rounded px-2 py-1 w-24"
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button
        type="button"
        onClick={() => onChange([...slabs, { from: 0, to: null, amount: 0 }])}
        className="mt-2 text-brand-700 text-xs underline"
      >
        Add slab
      </button>
    </div>
  );
};

const ComponentModal = ({ component, onClose, onSaved }) => {
  const isEdit = Boolean(component);
  const [form, setForm] = useState(() => ({
    code: component?.code ?? '',
    name: component?.name ?? '',
    type: component?.type ?? 'EARNING',
    calculation: component?.calculation ?? 'PERCENT_OF_GROSS',
    value: component?.value ?? 0,
    pf_applicable: component?.pf_applicable ?? false,
    esi_applicable: component?.esi_applicable ?? true,
    prorated: component?.prorated ?? true,
    sequence: component?.sequence ?? 1,
    is_active: component?.is_active ?? true
  }));

  const save = useMutation({
    mutationFn: (payload) => saveSalaryComponent(payload, component?.id),
    onSuccess: () => {
      toast.success(isEdit ? 'Component updated' : 'Component created');
      onSaved();
    },
    onError: (err) => toast.error(err?.response?.data?.message || 'Could not save')
  });

  const set = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm({ ...form, [key]: value });
  };

  const needsValue = form.calculation !== 'BALANCE';

  return (
    <Modal
      name={isEdit ? `Edit ${component.name}` : 'Add salary component'}
      onClose={onClose}
      width="460px"
    >
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name">
            <input
              type="text"
              value={form.name}
              onChange={set('name')}
              placeholder="House Rent Allowance"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Code">
            <input
              type="text"
              value={form.code}
              onChange={set('code')}
              disabled={isEdit}
              placeholder="HRA"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-100"
            />
            {isEdit && (
              <p className="text-xs text-gray-500 mt-1">
                Codes cannot change — payslips already issued refer to this one.
              </p>
            )}
          </Field>
        </div>

        <Field label="Calculation">
          <select
            value={form.calculation}
            onChange={set('calculation')}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
          >
            {CALCULATIONS.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </Field>

        {needsValue && (
          <Field
            label={form.calculation === 'FIXED' ? 'Amount' : 'Percentage'}
          >
            <input
              type="number"
              step="0.01"
              value={form.value}
              onChange={set('value')}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </Field>
        )}

        <Field label="Order">
          <input
            type="number"
            value={form.sequence}
            onChange={set('sequence')}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
        </Field>

        <Check
          label="Counts toward PF wages"
          checked={form.pf_applicable}
          onChange={set('pf_applicable')}
          hint="Usually basic only, but this is a policy decision — some companies include more."
        />
        <Check
          label="Counts toward ESI gross"
          checked={form.esi_applicable}
          onChange={set('esi_applicable')}
        />
        <Check
          label="Reduces with loss of pay"
          checked={form.prorated}
          onChange={set('prorated')}
          hint="Off for fixed reimbursements paid in full regardless of attendance."
        />

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded border border-gray-300 text-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={save.isPending}
            onClick={() => save.mutate({ ...form, value: Number(form.value) })}
            className="px-4 py-2 rounded bg-brand-700 text-white text-sm disabled:opacity-50"
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

const Section = ({ title, children }) => (
  <div>
    <div className="text-sm font-semibold text-gray-800 mb-2">{title}</div>
    {children}
  </div>
);

const Grid = ({ children }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-2">
    {children}
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
    {children}
  </div>
);

const Num = ({ label, value, onChange }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
    <input
      type="number"
      step="0.01"
      value={value ?? 0}
      onChange={onChange}
      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
    />
  </div>
);

const Check = ({ label, checked, onChange, hint }) => (
  <label className="flex items-start gap-2 text-sm text-gray-800">
    <input type="checkbox" checked={Boolean(checked)} onChange={onChange} className="mt-1" />
    <span>
      {label}
      {hint && <span className="block text-xs text-gray-500">{hint}</span>}
    </span>
  </label>
);

function defaultStatutory() {
  return {
    effective_from: new Date().toISOString().slice(0, 10),
    pf_enabled: false,
    pf_employee_rate: 12,
    pf_employer_rate: 12,
    pf_wage_ceiling: 15000,
    pf_restrict_to_ceiling: true,
    eps_rate: 8.33,
    eps_wage_ceiling: 15000,
    esi_enabled: false,
    esi_employee_rate: 0.75,
    esi_employer_rate: 3.25,
    esi_wage_ceiling: 21000,
    pt_enabled: false,
    pt_slabs: [],
    lwf_enabled: false,
    lwf_employee: 0,
    lwf_employer: 0
  };
}

function describeCalculation(component) {
  switch (component.calculation) {
    case 'FIXED':
      return `₹${Number(component.value)}`;
    case 'PERCENT_OF_GROSS':
      return `${Number(component.value)}% of gross`;
    case 'PERCENT_OF_BASIC':
      return `${Number(component.value)}% of basic`;
    case 'BALANCE':
      return 'Balance';
    default:
      return '—';
  }
}

function dateInput(value) {
  if (!value) return new Date().toISOString().slice(0, 10);
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

function money(value) {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n)) return '0';
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
}

function titleCase(value) {
  if (!value) return '—';
  return value.charAt(0) + value.slice(1).toLowerCase();
}

const Th = ({ children }) => (
  <th className="text-left font-medium px-4 py-3 whitespace-nowrap">{children}</th>
);
const Td = ({ children, className = '' }) => (
  <td className={`px-4 py-3 align-top ${className}`}>{children}</td>
);

export default PayrollSetup;
